import { centroid } from '@turf/turf';
import { getCerealsGrowthStageTranslation } from 'core/utils/translations/growth-stage';
import type { Feature, FeatureCollection, Geometry, Point } from 'geojson';
import { groupBy, isEmpty } from 'lodash';
import {
  RiskLevelEnum,
  type RegionalOverviewPointFeature,
  type RegionalOverviewPolygonFeature,
  type RegionalOverviewV2Properties
} from 'querys/regional-overview/regional-overview.query.models';
import type { IDataBBCH, IDataDetailedStatistics, IDataImpactedFields, MapPropertiesPointFeature } from './regional-risk.models';
import { RegionalRiskLevelLegend } from './regional-risk.models';

export const getCountPercentage = (count: number, total: number): number => {
  return Math.round((count / total) * 100) || 0;
};

export const getImpactedFieldsStatistics = (data: RegionalOverviewPointFeature[]): IDataImpactedFields[] => {
  const size = data.length;

  if (size === 0)
    return [
      { name: RegionalRiskLevelLegend.NO_RISK, value: '100' },
      { name: RegionalRiskLevelLegend.LOW_RISK, value: '0' },
      { name: RegionalRiskLevelLegend.MEDIUM_RISK, value: '0' },
      { name: RegionalRiskLevelLegend.HIGH_RISK, value: '0' }
    ];

  let noRiskCount = 0;
  let lowRiskCount = 0;
  let mediumRiskCount = 0;
  let highRiskCount = 0;

  data.forEach(item => {
    if (item.properties.calculatedRisk === RiskLevelEnum.NO_RISK) {
      noRiskCount++;
    } else if (item.properties.calculatedRisk === RiskLevelEnum.LOW) {
      lowRiskCount++;
    } else if (item.properties.calculatedRisk === RiskLevelEnum.MEDIUM) {
      mediumRiskCount++;
    } else if (item.properties.calculatedRisk === RiskLevelEnum.HIGH) {
      highRiskCount++;
    }
  });

  return [
    { name: RegionalRiskLevelLegend.NO_RISK, value: getCountPercentage(noRiskCount, size).toString() },
    { name: RegionalRiskLevelLegend.LOW_RISK, value: getCountPercentage(lowRiskCount, size).toString() },
    { name: RegionalRiskLevelLegend.MEDIUM_RISK, value: getCountPercentage(mediumRiskCount, size).toString() },
    { name: RegionalRiskLevelLegend.HIGH_RISK, value: getCountPercentage(highRiskCount, size).toString() }
  ];
};

export const getGrowthStagesStatistics = <T extends GeoJsonProperties>(features: Feature<Geometry, T>[]): IDataBBCH[] => {
  if (!features.length) return [];

  const bbchCount = new Map<string, number>();

  features.forEach(item => {
    if (!item.properties?.values?.risks?.prevBBCH) return;

    const count = bbchCount.get(item.properties.values.risks.prevBBCH) ?? 0;
    bbchCount.set(item.properties.values.risks.prevBBCH, count + 1);
  });

  const statisticsData: { prevBBCH: string; percentage: number }[] = [];
  for (const [key, value] of bbchCount.entries()) {
    statisticsData.push({
      prevBBCH: key,
      percentage: (value / features.length) * 100
    });
  }

  statisticsData.sort((a, b) => b.percentage - a.percentage);

  return statisticsData.map(item => ({
    prevBBCH: item.prevBBCH,
    percentage: item.percentage.toFixed(2),
    textBBCH: getCerealsGrowthStageTranslation(item.prevBBCH)
  }));
};

export const getDetailedStatistics = (features: RegionalOverviewPointFeature[]): IDataDetailedStatistics => {
  return {
    risksStatistics: getImpactedFieldsStatistics(features),
    bbchStatistics: getGrowthStagesStatistics(features)
  };
};

export const adapterCustomLabel = (textAll: string, isUppercase: boolean) => {
  const countLimite = 8;
  const text = textAll.toLowerCase();
  const textCapitalize = text ? text[0].toUpperCase() + text.slice(1, text.length) : '';
  const result = textCapitalize.length > countLimite ? `${textCapitalize.slice(0, countLimite)}...` : textCapitalize;
  return isUppercase ? result.toUpperCase() : result;
};

export const getPropertiesMarkers = (
  regionalRiskFeatures: RegionalOverviewPolygonFeature[]
): FeatureCollection<Point, MapPropertiesPointFeature> => {
  if (isEmpty(regionalRiskFeatures))
    return {
      type: 'FeatureCollection',
      features: []
    };

  const groupedFields = groupBy(regionalRiskFeatures, 'properties.values.property_id');

  const features = Object.entries(groupedFields)
    .map(([, fields]) => {
      const marker = fields[0];

      if (!marker.properties) {
        return undefined;
      }

      return {
        id: marker.properties.values?.property_id,
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            marker.properties.values?.property_latitude ? +marker.properties.values.property_latitude : 0,
            marker.properties.values?.property_longitude ? +marker.properties.values.property_longitude : 0
          ]
        },
        properties: {
          id: marker.properties.values?.property_id,
          icon: 'propertyRegionalRisk',
          propertyName: marker.properties.values?.property_name,
          amountFields: fields.length
        }
      };
    })
    .filter(Boolean) as Feature<Point, MapPropertiesPointFeature>[];

  return {
    type: 'FeatureCollection',
    features: features
  };
};

export const getFieldPoints = (
  features: RegionalOverviewPolygonFeature[] | RegionalOverviewPointFeature[]
): RegionalOverviewPointFeature[] => {
  return features.map(item => {
    const center = centroid<RegionalOverviewV2Properties>(item);
    return {
      type: 'Feature',
      geometry: center.geometry,
      properties: item.properties
    } as RegionalOverviewPointFeature;
  });
};
